import { Alert } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { Button } from "@/components/Button/Button";
import { Drawer } from "@/components/Drawer/Drawer";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { PaymentChooser } from "@/components/PaymentChooser/PaymentChooser";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { AdditionalInfoCard } from "@/features/flights/components/AdditionalInfoCard";
import { BaggageAllowance } from "@/features/flights/components/BaggageAllowance";
import BottomDrawer from "@/features/flights/components/flight-purchase/BottomDrawer";
import FlightDetailsCard from "@/features/flights/components/FlightDetailsCard/FlightDetailsCard";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { useSelectedDepartureFlight } from "@/features/flights/hooks/flightSearch/useSelectedDepartureFlight";
import { useSelectedReturnFlight } from "@/features/flights/hooks/flightSearch/useSelectedReturnFlight";
import { flightAvailabilityQueryOptions } from "@/features/flights/hooks/useFlightAvailability";
import { useCreatePendingInstallment } from "@/hooks/useCreatePendingInstallment";
import {
  BnplTrackingEvents,
  FlightTrackingEvents,
  rudderAnalytics,
} from "@/lib/rudderstack";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute(
  "/_authenticated/flights/_flights/_flight-offer/offer/$id/step/1",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const { selectedFlightPayload } = useFlightSelectionPayloadContext();
  const { result: selectedDepartureFlight } = useSelectedDepartureFlight();
  const { result: selectedReturnFlight } = useSelectedReturnFlight();
  const navigate = Route.useNavigate();
  const intl = useIntl();

  const { data: flightAvailabilityData, isLoading: isAdditionalDataLoading } =
    useQuery(flightAvailabilityQueryOptions(selectedFlightPayload));

  const {
    paymentType,
    selectedInstallments,
    maxAvailableInstallments,
    isEligibilityLoading,
    refreshSession,
    isEligible,
  } = usePaymentContext();

  const [isContinuing, setIsContinuing] = useState(false);
  const [showPaymentChooser, setShowPaymentChooser] = useState(false);
  const isBnplFeatureEnabled = useFeatureIsOn("bnpl") && Boolean(isEligible);

  const {
    mutateAsync: createPendingInstallment,
    isError: isCreatePendingInstallmentError,
  } = useCreatePendingInstallment();

  const originalPrice = flightAvailabilityData?.totalPrice || 0;

  // Calculate BNPL fee if BNPL is selected
  const includedBnplFee = includeBnplFee(originalPrice);
  const totalPrice = paymentType === "bnpl" ? includedBnplFee : originalPrice;

  const handleContinue = () => {
    if (isBnplFeatureEnabled) {
      setShowPaymentChooser(true);
      return;
    }
    if (isContinuing) return;
    setIsContinuing(true);

    rudderAnalytics.track(
      FlightTrackingEvents.USER_CONTINUED_AFTER_FLIGHT_DETAILS,
      {
        bookingType: "Flight",
        selected_offer_id: flightAvailabilityData?.index,
        price: totalPrice,
      },
    );
    navigate({ to: "/flights/offer/$id/step/2" });
    setIsContinuing(false);
  };

  const handlePaymentChooserContinue = async () => {
    if (isContinuing) return;
    setIsContinuing(true);

    rudderAnalytics.track(
      FlightTrackingEvents.USER_CONTINUED_AFTER_FLIGHT_DETAILS,
      {
        bookingType: "Flight",
        selected_offer_id: flightAvailabilityData?.index,
        price: totalPrice,
      },
    );

    const eventName =
      paymentType === "bnpl"
        ? BnplTrackingEvents.USER_SELECTED_BNPL_PAYMENT
        : BnplTrackingEvents.USER_SELECTED_STANDARD_PAYMENT;

    rudderAnalytics.track(eventName, {
      is_bnpl_eligible: isEligible,
    });

    try {
      if (paymentType === "bnpl") {
        const { data: eligibilityData } = await refreshSession();
        if (eligibilityData?.isEligible) {
          await createPendingInstallment({
            sessionId: eligibilityData.sessionId,
            amount: originalPrice,
            countOfMonth: selectedInstallments,
          });
        }
      }
      navigate({ to: "/flights/offer/$id/step/2" });
    } finally {
      setIsContinuing(false);
      setShowPaymentChooser(false);
    }
  };

  return (
    <>
      <div className="px-4">
        <div className="mt-2 mb-4">
          {selectedDepartureFlight && (
            <div className="flex flex-col gap-2">
              <div className="section-title mb-0">
                {selectedReturnFlight
                  ? intl.formatMessage({ id: "flights.departureFlight" })
                  : intl.formatMessage({ id: "flights.flightDetails" })}
              </div>
              <FlightDetailsCard
                flight={selectedDepartureFlight}
                offerItems={selectedFlightPayload?.offerItems}
                isDepartureFlight
              />
              <BaggageAllowance
                baggageData={flightAvailabilityData?.outboundBaggage}
                isLoading={isAdditionalDataLoading}
              />
            </div>
          )}

          {selectedReturnFlight && (
            <div className="mt-4">
              <div className="flex flex-col gap-2">
                <div className="section-title mb-0">
                  {intl.formatMessage({ id: "flights.returnFlight" })}
                </div>
                <FlightDetailsCard
                  flight={selectedReturnFlight}
                  offerItems={selectedFlightPayload?.offerItems}
                />
                {flightAvailabilityData?.returnBaggage !== null ? (
                  <BaggageAllowance
                    baggageData={flightAvailabilityData?.returnBaggage}
                    isLoading={isAdditionalDataLoading}
                  />
                ) : null}
              </div>
            </div>
          )}
        </div>

        <AdditionalInfoCard />
      </div>

      <BottomDrawer
        totalPrice={formatCurrency(originalPrice)}
        showPayLater
        payLaterPrice={formatCurrency(
          calculateMonthlyInstallment(
            includedBnplFee,
            maxAvailableInstallments,
          ),
        )}
        buttonLabel={intl.formatMessage({ id: "flights.continue" })}
        onButtonClick={handleContinue}
        disabled={
          isAdditionalDataLoading || isContinuing || isEligibilityLoading
        }
      />
      {isCreatePendingInstallmentError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {intl.formatMessage({ id: "payment.pendingInstallmentError" })}
        </Alert>
      )}

      <Drawer
        open={showPaymentChooser}
        onClose={() => setShowPaymentChooser(false)}
      >
        <DrawerTopBar
          onCloseClick={() => setShowPaymentChooser(false)}
          title={intl.formatMessage({ id: "payment.paymentTypeHeader" })}
        />

        <PaymentChooser price={originalPrice} />

        <Button
          fullWidth
          variant="contained"
          onClick={handlePaymentChooserContinue}
          disabled={isContinuing}
        >
          {intl.formatMessage({ id: "flights.continue" })}
        </Button>
      </Drawer>
    </>
  );
}
